import { useState } from 'react';
import { useAuth } from '../lib/authContext';

export default function SignInModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signInWithMagicLink, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  if (!open) return null;

  const handleMagic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setErr(null);
    const { error } = await signInWithMagicLink(email.trim());
    setBusy(false);
    if (error) setErr(typeof error === 'string' ? error : (error as any).message ?? 'Something went wrong.');
    else setSent(true);
  };

  const handleGoogle = async () => {
    setGoogleBusy(true);
    await signInWithGoogle();
  };

  const F = "'Plus Jakarta Sans', system-ui, sans-serif";
  const FS = "'Cormorant Garamond', Georgia, serif";

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:9999,
        background:'rgba(26,18,8,0.72)',
        backdropFilter:'blur(6px)',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:'clamp(12px,4vw,24px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:'#faf7f0',
          borderRadius:'20px',
          width:'100%', maxWidth:'420px',
          boxShadow:'0 24px 64px rgba(26,18,8,0.35), 0 0 0 1px rgba(196,154,60,0.2)',
          overflow:'hidden', position:'relative', fontFamily:F,
        }}
      >
        <div style={{ height:'4px', background:'linear-gradient(90deg,#c49a3c,#e8b84b,#c49a3c)' }} />
        <button onClick={onClose} aria-label="Close"
          style={{ position:'absolute', top:'16px', right:'16px', background:'rgba(196,154,60,0.1)', border:'none', width:'32px', height:'32px', borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', color:'#6b5c3e', lineHeight:1 }}>
          ×
        </button>
        <div style={{ padding:'clamp(24px,5vw,36px) clamp(24px,5vw,36px) clamp(20px,4vw,28px)' }}>
          <div style={{ textAlign:'center', marginBottom:'24px' }}>
            <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'linear-gradient(135deg,#231508,#3a2810)', border:'2px solid rgba(196,154,60,0.35)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px', fontSize:'24px' }}>🎵</div>
            <h2 style={{ fontFamily:FS, fontSize:'clamp(22px,5vw,28px)', fontWeight:700, color:'#1a1208', margin:'0 0 6px' }}>Sign in to RaagEngine</h2>
            <p style={{ fontSize:'13px', color:'#7a6355', margin:0, lineHeight:1.5 }}>Free &amp; unlimited · no card needed</p>
          </div>

          {sent ? (
            <div style={{ textAlign:'center', padding:'8px 0 12px' }}>
              <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'linear-gradient(135deg,rgba(196,154,60,0.15),rgba(196,154,60,0.08))', border:'2px solid rgba(196,154,60,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:'28px' }}>✉️</div>
              <p style={{ fontFamily:FS, fontSize:'22px', fontWeight:700, color:'#1a1208', margin:'0 0 8px' }}>Check your inbox</p>
              <p style={{ fontSize:'13px', color:'#7a6355', margin:'0 0 6px', lineHeight:1.6 }}>We sent a magic link to</p>
              <p style={{ fontSize:'14px', fontWeight:700, color:'#1a1208', margin:'0 0 16px', background:'rgba(196,154,60,0.1)', padding:'6px 14px', borderRadius:'20px', display:'inline-block' }}>{email}</p>
              <p style={{ fontSize:'12px', color:'#9a8570', lineHeight:1.65, maxWidth:'300px', margin:'0 auto' }}>Click the link in the email to sign in instantly. No password ever needed. Check your spam folder if you don't see it within 2 minutes.</p>
              <button onClick={() => { setSent(false); setEmail(''); }}
                style={{ marginTop:'20px', background:'none', border:'none', cursor:'pointer', fontSize:'12px', color:'#c49a3c', fontFamily:F, fontWeight:600 }}>
                ← Use a different email
              </button>
            </div>
          ) : (
            <>
              {/* Google OAuth - primary CTA */}
              <button
                onClick={handleGoogle}
                disabled={googleBusy}
                style={{
                  width:'100%', height:'50px',
                  background:'#fff',
                  border:'1.5px solid #e0d8cc',
                  borderRadius:'12px',
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'10px',
                  cursor: googleBusy ? 'not-allowed' : 'pointer',
                  fontFamily:F, fontSize:'15px', fontWeight:600, color:'#1a1208',
                  marginBottom:'14px',
                  boxShadow:'0 2px 8px rgba(0,0,0,0.06)',
                  transition:'box-shadow .15s, transform .15s',
                  opacity: googleBusy ? 0.7 : 1,
                }}
                onMouseOver={e => { if (!googleBusy) { e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,0.12)'; e.currentTarget.style.transform='translateY(-1px)'; }}}
                onMouseOut={e => { e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.06)'; e.currentTarget.style.transform=''; }}
              >
                {googleBusy ? '⏳ Connecting…' : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>

              {/* Divider */}
              <div style={{ display:'flex', alignItems:'center', gap:'10px', margin:'4px 0 14px' }}>
                <div style={{ flex:1, height:'1px', background:'rgba(196,154,60,0.2)' }} />
                <span style={{ fontSize:'12px', color:'#9a8570', fontWeight:600 }}>or use email</span>
                <div style={{ flex:1, height:'1px', background:'rgba(196,154,60,0.2)' }} />
              </div>

              <form onSubmit={handleMagic}>
                <label style={{ display:'block', fontSize:'12px', fontWeight:700, color:'#6b5c3e', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:'8px' }}>Your email</label>
                <input
                  type="email" placeholder="you@email.com" value={email}
                  onChange={e => setEmail(e.target.value)} required
                  style={{ width:'100%', height:'48px', padding:'0 16px', fontSize:'15px', fontFamily:F, color:'#1a1208', background:'#fff', border:'1.5px solid rgba(196,154,60,0.3)', borderRadius:'12px', outline:'none', boxSizing:'border-box', marginBottom:'4px', transition:'border-color .2s' }}
                  onFocus={e => (e.currentTarget.style.borderColor='#c49a3c')}
                  onBlur={e => (e.currentTarget.style.borderColor='rgba(196,154,60,0.3)')}
                />
                {err && <p style={{ fontSize:'12px', color:'#b43232', margin:'6px 0 0', lineHeight:1.4 }}>⚠ {err}</p>}
                <button type="submit" disabled={busy}
                  style={{ marginTop:'14px', width:'100%', height:'50px', background: busy ? 'rgba(196,154,60,0.4)' : 'linear-gradient(135deg,#c49a3c,#e8b84b)', color:'#1a1208', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily:F, letterSpacing:'0.02em', transition:'transform .15s, box-shadow .15s', boxShadow: busy ? 'none' : '0 4px 16px rgba(196,154,60,0.3)' }}
                  onMouseOver={e => { if (!busy) { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(196,154,60,0.4)'; }}}
                  onMouseOut={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=busy?'none':'0 4px 16px rgba(196,154,60,0.3)'; }}>
                  {busy ? '⏳ Sending…' : '✉ Send magic link'}
                </button>
                <div style={{ display:'flex', gap:'6px', justifyContent:'center', flexWrap:'wrap', marginTop:'16px' }}>
                  {['Free & unlimited','No password','No spam'].map(t => (
                    <span key={t} style={{ fontSize:'11px', color:'#7a6355', background:'rgba(196,154,60,0.08)', border:'1px solid rgba(196,154,60,0.15)', padding:'3px 10px', borderRadius:'20px' }}>✓ {t}</span>
                  ))}
                </div>
              </form>
            </>
          )}

          <p style={{ fontSize:'11px', color:'#aaa090', textAlign:'center', marginTop:'20px', lineHeight:1.6 }}>
            By signing in you agree to our{' '}
            <a href="/terms/" style={{ color:'#c49a3c', textDecoration:'none' }}>Terms</a>{' '}and{' '}
            <a href="/privacy/" style={{ color:'#c49a3c', textDecoration:'none' }}>Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
